import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLogoutMutation } from '@/api/generated/authApi';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setColorMode, setMobileSidebarOpen } from '@/app/uiSlice';
import { selectCurrentUser, sessionCleared } from '@/features/auth/authSlice';

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

/** Every signed-in user is a vendor; the chip says so under their business name. */
const ROLE_LABEL = 'Vendor';
const AVATAR_GRADIENT = 'linear-gradient(140deg,#0FB5A6,#188A5A)';

function LogoMark({ size = 28 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: `${size * 0.27}px`,
        background: 'linear-gradient(140deg, #0FB5A6, #2A47C9)',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        boxShadow: '0 3px 10px -3px rgba(15,181,166,.5)',
      }}
    >
      <svg viewBox="0 0 24 24" fill="none" width={size * 0.58} height={size * 0.58}>
        <path
          d="M3 8c4 0 4 3 9 3s5-3 9-3M3 15c4 0 4 3 9 3s5-3 9-3"
          stroke="#fff"
          strokeWidth={2}
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
}

export function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const colorMode = useAppSelector((s) => s.ui.colorMode);
  const user = useAppSelector(selectCurrentUser);
  const [logout] = useLogoutMutation();

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);

  async function handleSignOut() {
    setUserMenuAnchor(null);
    try {
      await logout().unwrap();
    } catch {
      /* local sign-out anyway */
    }
    dispatch(sessionCleared());
    navigate('/login', { replace: true });
  }

  const userInitials = user?.displayName ? getInitials(user.displayName) : '?';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 1.5, minHeight: '64px !important', px: { xs: 2, md: 3 } }}>
        {/* Mobile hamburger */}
        {isMobile && (
          <IconButton
            edge="start"
            onClick={() => dispatch(setMobileSidebarOpen(true))}
            aria-label="Open navigation"
            sx={{ color: 'text.secondary' }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo + brand name on the left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <LogoMark size={30} />
          <Typography
            sx={{
              fontFamily: (t) => t.otrack.displayFamily,
              fontWeight: 700,
              fontSize: '1.125rem',
              letterSpacing: '-0.01em',
              lineHeight: 1,
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            OTrack
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Theme toggle */}
        <Tooltip title={colorMode === 'dark' ? 'Light mode' : 'Dark mode'}>
          <IconButton
            onClick={() => dispatch(setColorMode(colorMode === 'dark' ? 'light' : 'dark'))}
            aria-label="Toggle colour mode"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            {colorMode === 'dark' ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton
            aria-label="Notifications"
            size="small"
            sx={{ color: 'text.secondary' }}
            onClick={(e) => setNotifAnchor(e.currentTarget)}
          >
            <Badge
              color="error"
              variant="dot"
              overlap="circular"
              sx={{ '& .MuiBadge-dot': { border: '2px solid', borderColor: 'background.paper' } }}
            >
              <NotificationsNoneIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={notifAnchor}
          open={Boolean(notifAnchor)}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { width: 300, mt: 1 } } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2">Notifications</Typography>
          </Box>
          <Divider />
          <MenuItem disabled sx={{ py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              No new notifications.
            </Typography>
          </MenuItem>
        </Menu>

        {/* User chip */}
        <Box
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '5px',
            py: '5px',
            pr: '12px',
            borderRadius: '99px',
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: 13,
              fontFamily: (t) => t.otrack.displayFamily,
              fontWeight: 700,
              background: AVATAR_GRADIENT,
              color: '#fff',
            }}
          >
            {userInitials}
          </Avatar>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              lineHeight: 1.2,
            }}
          >
            <Typography variant="caption" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {user?.displayName ?? 'Signed in'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.3, fontSize: 11 }}
            >
              {ROLE_LABEL}
            </Typography>
          </Box>
        </Box>

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {user ? (
            <Box>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">{user.displayName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ROLE_LABEL}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Box>
          ) : (
            <MenuItem disabled>Not signed in</MenuItem>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
